import { config } from '../config/environment';
import { authService } from './authService';

// Discount interface based on backend response
export interface Discount {
  percentage: number;
  code: string;
  description: string;
  requirement: string;
}

// Backend response structure for available discounts
export interface DiscountsResponse {
  discounts: Discount[];
}

// Service to handle discount-related operations
class DiscountService {
  private baseUrl: string;

  constructor() {
    this.baseUrl = config.authServerUrl;
  }

  /**
   * Fetch available discount codes from backend
   */
  async getAvailableDiscounts(): Promise<Discount[]> {
    try {
      console.log('🎫 Fetching available discounts from backend...');
      
      const response = await authService.getApiInstance().get('/api/discounts/available');
      
      console.log('✅ Available discounts fetched successfully:', response.data.discounts);
      
      return response.data.discounts;
    } catch (error) {
      console.error('❌ Error fetching available discounts:', error);
      
      // Return fallback discounts if backend is unavailable
      return this.getFallbackDiscounts();
    }
  }

  /**
   * Validate a discount code with backend
   */
  async validateDiscountCode(code: string, cartItems?: any[]): Promise<{ valid: boolean; discount?: Discount; message?: string }> {
    try {
      console.log('🔍 Validating discount code with backend:', code);
      
      // Calculate item count from cart items
      const itemCount = cartItems ? cartItems.reduce((sum, item) => sum + (item.quantity || 1), 0) : 1;
      
      const payload = {
        discountCode: code.trim().toUpperCase(),
        itemCount: itemCount
      };

      const response = await authService.getApiInstance().post('/api/discounts/validate', payload);
      
      const data = response.data;
      console.log('✅ Discount code validated successfully:', data);
      
      if (data.valid) {
        // Convert backend response to our Discount interface
        const discount: Discount = {
          code: data.discountCode,
          percentage: data.discountPercentage,
          description: data.discountDescription,
          requirement: 'Backend validated discount'
        };
        
        return {
          valid: true,
          discount,
          message: data.message
        };
      } else {
        return {
          valid: false,
          message: data.message
        };
      }
      
    } catch (error: any) {
      console.error('❌ Error validating discount code:', error);
      
      // Check if it's an HTTP error with a response
      if (error.response?.data?.message) {
        return { 
          valid: false, 
          message: error.response.data.message
        };
      }
      
      // Fallback to local validation if backend is unavailable
      return this.validateDiscountCodeLocally(code);
    }
  }

  /**
   * Local fallback validation using available discounts
   */
  private async validateDiscountCodeLocally(code: string): Promise<{ valid: boolean; discount?: Discount; message?: string }> {
    try {
      const availableDiscounts = await this.getAvailableDiscounts();
      const normalizedCode = code.trim().toUpperCase();
      
      const discount = availableDiscounts.find(d => d.code.toUpperCase() === normalizedCode);
      
      if (discount) {
        return {
          valid: true,
          discount,
          message: 'Discount applied successfully (offline validation)'
        };
      } else {
        return {
          valid: false,
          message: 'Invalid discount code'
        };
      }
    } catch (error) {
      console.error('❌ Error in local discount validation:', error);
      return {
        valid: false,
        message: 'Unable to validate discount code at this time'
      };
    }
  }

  /**
   * Fallback discounts when backend is not available
   */
  private getFallbackDiscounts(): Discount[] {
    return [
      {
        percentage: 5,
        code: "WELCOME5",
        description: "5% discount for first-time users",
        requirement: "First-time users only"
      },
      {
        percentage: 15,
        code: "GROUPBOOKING15",
        description: "15% discount for group bookings",
        requirement: "Multiple service items required"
      },
      {
        percentage: 10,
        code: "LOYALTY10",
        description: "10% discount for loyal customers",
        requirement: "Customers with 3+ completed bookings"
      },
      {
        percentage: 5,
        code: "ANTARYATRA5",
        description: "5% Antaryatra discount",
        requirement: "General discount code"
      }
    ];
  }

  /**
   * Calculate discount amount based on percentage and subtotal
   */
  calculateDiscountAmount(discount: Discount, subtotal: number): number {
    return (subtotal * discount.percentage) / 100;
  }

  /**
   * Format discount for display
   */
  formatDiscountDisplay(discount: Discount): string {
    return `${discount.code} - ${discount.percentage}% off (${discount.description})`;
  }
}

// Export singleton instance
export const discountService = new DiscountService();
export default discountService;